import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, AlertTriangle, Stethoscope, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SEO from "@/components/SEO";

type Topic = {
  id: string; slug: string; title: string; pillar: number; topic_type: string;
  summary_plain: string; body_md: string | null; evidence_level: string | null;
  evidence_rationale: string | null; red_flags_md: string | null; doctor_questions_md: string | null;
  reviewed_at: string | null;
};

const PILLAR_NAMES: Record<number, string> = {
  1: "Understanding Menopause", 2: "Symptoms", 3: "Treatments", 4: "Healthy Ageing",
  5: "Lifestyle", 6: "Mental Health", 7: "Relationships", 8: "Diversity",
  9: "Medicines", 10: "Practical Guides",
};

function mdToHtml(md: string): string {
  // minimal safe markdown: paragraphs, bullets, bold
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let inList = false;
  for (const raw of lines) {
    const l = esc(raw).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^\s*[-*]\s+/.test(l)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${l.replace(/^\s*[-*]\s+/, "")}</li>`);
    } else if (l.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else if (/^#{1,3}\s+/.test(l)) {
      if (inList) { out.push("</ul>"); inList = false; }
      const level = l.match(/^#+/)![0].length;
      out.push(`<h${level + 2} class="font-serif text-2xl mt-6 mb-2">${l.replace(/^#+\s+/, "")}</h${level + 2}>`);
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${l}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

export default function KnowledgeTopic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [tier, setTier] = useState<{ emoji: string; label: string; description: string } | null>(null);
  const [sources, setSources] = useState<any[]>([]);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: t } = await supabase
        .from("knowledge_topics").select("*")
        .eq("slug", slug!).eq("status", "approved").maybeSingle();
      if (!t) { setLoading(false); return; }
      setTopic(t as Topic);

      const [tierRes, srcRes, linkRes] = await Promise.all([
        t.evidence_level ? supabase.from("knowledge_evidence_tiers").select("*").eq("code", t.evidence_level).maybeSingle() : Promise.resolve({ data: null }),
        supabase.from("knowledge_topic_sources")
          .select("id, citation_title, organisation, url, published_at, tier, document:medical_documents(title, organisation, jurisdiction, source_url, version)")
          .eq("topic_id", t.id),
        supabase.from("knowledge_topic_links")
          .select("relation, sort_order, to:knowledge_topics!knowledge_topic_links_to_topic_id_fkey(id, slug, title, topic_type, summary_plain, status)")
          .eq("from_topic_id", t.id).order("sort_order"),
      ]);
      setTier(tierRes.data as any);
      setSources(srcRes.data ?? []);
      setRelated((linkRes.data ?? []).filter((l: any) => l.to?.status === "approved"));
      setLoading(false);
    })();
  }, [slug]);

  const submitFeedback = async () => {
    if (!topic || !feedback.trim()) return;
    setSubmitting(true);
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast({ title: "Please sign in to send feedback", variant: "destructive" });
      setSubmitting(false); return;
    }
    const { error } = await supabase.from("knowledge_feedback").insert({
      topic_id: topic.id, user_id: user.user.id, message: feedback.trim(),
    });
    setSubmitting(false);
    if (error) { toast({ title: "Could not send", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Thank you — your note reached our editorial team." });
    setFeedback("");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!topic) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-4 text-center">
      <h1 className="font-serif text-3xl">Topic not found</h1>
      <p className="text-muted-foreground">This page may still be in draft or has been retired.</p>
      <Button variant="outline" onClick={() => navigate("/knowledge")}>Back to Knowledge</Button>
    </div>
  );

  const relatedGroups = related.reduce((acc: Record<string, any[]>, l) => {
    (acc[l.relation] ||= []).push(l.to); return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <SEO
        title={topic.title}
        description={(topic.summary_plain || `${topic.title} — a Her Season knowledge topic.`).slice(0, 160)}
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: topic.title,
          description: topic.summary_plain,
          author: { "@type": "Organization", name: "Her Season" },
          publisher: { "@type": "Organization", name: "Her Season" },
          datePublished: topic.reviewed_at || undefined,
        }}
      />
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-4 gap-2" onClick={() => navigate("/knowledge")}>
          <ArrowLeft className="h-4 w-4" /> Knowledge
        </Button>

        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <Badge variant="secondary">Pillar {topic.pillar} · {PILLAR_NAMES[topic.pillar]}</Badge>
          <Badge variant="outline">{topic.topic_type}</Badge>
          {tier && <span title={tier.description} className="text-sm inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-foreground/70">
            {tier.label}
          </span>}
        </div>

        <h1 className="font-serif text-4xl md:text-5xl mb-4">{topic.title}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">{topic.summary_plain}</p>

        {topic.body_md && (
          <div className="prose prose-slate max-w-none [&_p]:my-3 [&_ul]:my-3 [&_li]:my-1"
               dangerouslySetInnerHTML={{ __html: mdToHtml(topic.body_md) }} />
        )}

        {topic.red_flags_md && (
          <Card className="my-8 border-destructive/40 bg-destructive/5">
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <CardTitle className="text-lg">When to see a clinician</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: mdToHtml(topic.red_flags_md) }} />
            </CardContent>
          </Card>
        )}

        {topic.doctor_questions_md && (
          <Card className="my-6">
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
              <Stethoscope className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Doctor discussion points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: mdToHtml(topic.doctor_questions_md) }} />
            </CardContent>
          </Card>
        )}

        {Object.keys(relatedGroups).length > 0 && (
          <div className="my-10">
            <h2 className="font-serif text-2xl mb-4">Related</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(relatedGroups).flatMap(([rel, items]) => (items as any[]).map((r: any) => (
                <Link key={rel + r.id} to={`/knowledge/${r.slug}`}>
                  <Card className="hover:shadow-md transition-shadow h-full">
                    <CardContent className="p-4">
                      <div className="text-xs text-muted-foreground mb-1">{rel.replace(/_/g, " ")}</div>
                      <div className="font-medium">{r.title}</div>
                      {r.summary_plain && <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.summary_plain}</div>}
                    </CardContent>
                  </Card>
                </Link>
              )))}
            </div>
          </div>
        )}

        {sources.length > 0 && (
          <Card className="my-8">
            <CardHeader><CardTitle className="text-lg">Sources</CardTitle></CardHeader>
            <CardContent>
              <ul className="text-sm space-y-2">
                {sources.map(s => {
                  const label = s.document?.title || s.citation_title;
                  const org = s.document?.organisation || s.organisation;
                  const url = s.document?.source_url || s.url;
                  const date = s.document?.published_at || s.published_at;
                  return (
                    <li key={s.id}>
                      <span className="font-medium">{org}</span> — {url ? <a className="underline" href={url} target="_blank" rel="noreferrer">{label}</a> : label}
                      {date && <span className="text-muted-foreground"> · {new Date(date).getFullYear()}</span>}
                      {s.tier && <Badge variant="outline" className="ml-2 text-xs">{s.tier}</Badge>}
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {topic.reviewed_at && (
          <p className="text-xs text-muted-foreground mb-6">Last reviewed {new Date(topic.reviewed_at).toLocaleDateString()}.</p>
        )}

        <Card className="my-8 bg-secondary/40">
          <CardHeader className="pb-2"><CardTitle className="text-lg">Suggest an improvement</CardTitle></CardHeader>
          <CardContent>
            <Textarea rows={3} value={feedback} onChange={e => setFeedback(e.target.value)}
              placeholder="Spotted something unclear or missing? Our editorial team reads every note." />
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={submitFeedback} disabled={submitting || !feedback.trim()}>
                {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
